import { isJson } from 'helper';
import { toastError, toastSuccess } from 'partial/components/react-geek-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  addOrganization,
  editOrganization,
  getSubscriberRequirements,
  getSubscribers,
  showSubscriber,
  updateRequirements,
} from './actions';

export const usePendingOnboardingCount = () => {
  const { data: raw } = useQuery({
    queryKey: [
      'get-pending-onboarding-count',
      { status: 'PENDING', per_page: 1 },
    ],
    queryFn: getSubscribers,
    retry: false,
    staleTime: 30000,
    refetchInterval: 60000,
  });
  return raw?.data?.meta?.total || 0;
};

export const useSubscribersList = (params) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['get-subscribers-list', params],
    queryFn: getSubscribers,
    retry: false,
    keepPreviousData: true,
  });
  const data = raw?.data?.data;
  const meta = raw?.data?.meta;
  return [isLoading, data, meta];
};

export const useShowSubscriber = (id) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['show-subscriber-info', id],
    queryFn: showSubscriber,
    retry: false,
    keepPreviousData: false,
    enabled: !!id,
  });
  const data = raw?.data?.data;
  return [isLoading, data];
};

export const useShowRequirements = (id) => {
  const { data: raw, isLoading } = useQuery({
    queryKey: ['show-subscriber-requirements', id],
    queryFn: getSubscriberRequirements,
    retry: false,
    keepPreviousData: true,
  });
  const temp = raw?.data?.data;
  const data = {
    ...temp,
    government_id: isJson(temp?.government_id)
      ? JSON.parse(temp?.government_id)
      : temp?.government_id,
    secondary_government_id: isJson(temp?.secondary_government_id)
      ? JSON.parse(temp?.secondary_government_id)
      : temp?.secondary_government_id,
  };
  return [isLoading, data];
};

export const useUpdateRequirement = (onSuccess, onError) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(updateRequirements, {
    onSuccess: () => {
      queryClient.invalidateQueries('get-subscribers-list');
      queryClient.invalidateQueries('show-subscriber-info');
      toastSuccess('Successfully updated verification!');
      if (typeof onSuccess === 'function') onSuccess();
    },
    onError: (err) => {
      toastError(err?.response?.data?.message);
      if (typeof onError === 'function') onError();
    },
  });
  return [isLoading, mutate];
};

export const useAddOrganization = (onSuccess, onError) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(addOrganization, {
    onSuccess: (res) => {
      queryClient.invalidateQueries('get-subscribers-list');
      queryClient.invalidateQueries('show-subscriber-info');
      toastSuccess('Successfully added organization!');
      if (typeof onSuccess === 'function') onSuccess(res);
    },
    onError: (err) => {
      toastError(err?.response?.data?.message);
      if (typeof onError === 'function') onError();
    },
  });
  return [isLoading, mutate];
};

export const useEditOrganization = (onSuccess) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(editOrganization, {
    onSuccess: (res) => {
      queryClient.invalidateQueries('get-subscribers-list');
      queryClient.invalidateQueries('show-subscriber-info');
      toastSuccess('Successfully updated organization!');
      if (typeof onSuccess === 'function') onSuccess(res);
    },
    onError: (err) => toastError(err?.response?.data?.message),
  });
  return [isLoading, mutate];
};
